import { useEffect, useState } from 'react';

/**
 * Fetches Google Maps location suggestions based on user input.
 * This hook manages debouncing and session handling for autocomplete requests.
 *
 * @param {string} inputValue - The current text from the search input.
 * @param {object | null} sessionToken - The active AutocompleteSessionToken from the search session.
 * @param {boolean} isApiReady - A flag indicating if the Google Maps API script has loaded.
 */
export const useFetchLocationSuggestions = (
	inputValue,
	sessionToken,
	isApiReady
) => {
	const [suggestions, setSuggestions] = useState([]);

	useEffect(() => {
		// No active session / API not ready
		if (!isApiReady || !sessionToken) {
			setSuggestions([]);
			return null;
		}
		// Debounce the API call to avoid sending a request on every keystroke.
		const timeoutId = setTimeout(() => {
			const autocompleteService =
				new window.google.maps.places.AutocompleteService();

			const request = {
				input: inputValue.trim(),
				language: 'en',
				types: ['(cities)'],
				componentRestrictions: { country: 'us' },
				sessionToken
			};

			autocompleteService.getPlacePredictions(
				request,
				(predictions, status) => {
					if (
						status ===
							window.google.maps.places.PlacesServiceStatus.OK &&
						predictions
					) {
						// Transform to match the structure expected by UI
						const transformed = predictions.map((pred) => ({
							placePrediction: {
								placeId: pred.place_id,
								text: { text: pred.description }
							}
						}));
						setSuggestions(transformed);
					} else {
						setSuggestions([]);
					}
				}
			);
		}, 300); // Wait 300ms after the user stops typing.

		return () => {
			clearTimeout(timeoutId);
		};
	}, [inputValue, isApiReady, sessionToken]);

	return { suggestions };
};
